import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Home, Search } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import api from "../lib/api";

export default function NotFoundPage() {
    useSEO({
        title: "Page Not Found — Carbon Labs Australia",
        description:
            "We couldn't find that page. Browse our full catalogue of premium Australian research products or head back home.",
        canonical: "https://www.carbonlabsau.com/404",
    });

    const [popular, setPopular] = useState([]);

    useEffect(() => {
        api.get("/products").then((r) => setPopular((r.data || []).slice(0, 4))).catch(() => {});
    }, []);

    return (
        <main className="min-h-[70vh] container-x py-24" data-testid="not-found-page">
            <div className="max-w-2xl">
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[var(--tiffany)] mb-3">// 404</p>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter mb-4">
                    Page not <span className="text-gold-gradient">found</span>
                </h1>
                <p className="text-base text-[var(--text-secondary)] mb-8 max-w-lg leading-relaxed">
                    The page you're looking for has moved, been renamed, or never existed on
                    Carbon Labs Australia. Here are some helpful next steps:
                </p>
                <div className="flex flex-wrap gap-3 mb-10">
                    <Link to="/" className="btn-solid" data-testid="404-home">
                        <Home size={14} /> Return home
                    </Link>
                    <Link to="/shop" className="btn-neon" data-testid="404-shop">
                        <Search size={14} /> Browse the shop
                    </Link>
                    <Link to="/contact" className="btn-ghost" data-testid="404-contact">
                        Contact support <ArrowRight size={12} />
                    </Link>
                </div>
            </div>

            {popular.length > 0 && (
                <section aria-labelledby="popular-heading">
                    <h2 id="popular-heading" className="text-sm font-mono uppercase tracking-widest text-[var(--text-muted)] mb-4">
                        Popular products
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {popular.map((p) => (
                            <Link
                                key={p.slug}
                                to={`/product/${p.slug}`}
                                className="rounded-2xl p-4 glass border border-white/5 hover-lift group"
                                data-testid={`404-suggestion-${p.slug}`}
                            >
                                <img
                                    src={p.image}
                                    alt={`${p.name} — Carbon Labs Australia`}
                                    loading="lazy"
                                    className="w-full aspect-square object-contain rounded-xl bg-black/40 mb-3"
                                />
                                <p className="font-display text-sm font-bold text-white group-hover:text-[var(--gold)] transition-colors">
                                    {p.name}
                                </p>
                                <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">
                                    {p.dosage}
                                </p>
                            </Link>
                        ))}
                    </div>
                </section>
            )}
        </main>
    );
}
